import React from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Stack,
  Box,
  Alert,
} from "@mui/material";
import {
  ErrorOutline as ErrorOutlineIcon,
  Payment as PaymentIcon,
  Home as HomeIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const PaymentErrorDialog = ({ open, errorData, onRetry }) => {
  const navigate = useNavigate();

  const getErrorConfig = () => {
    const status = errorData?.status || "UNKNOWN";
    
    switch (status) {
      case "EXPIRED":
        return {
          icon: <ErrorOutlineIcon sx={{ fontSize: 64, color: "#d32f2f" }} />,
          title: "Restaurant Subscription Expired",
          message: errorData?.message || "This restaurant's subscription has expired. Please contact the restaurant owner.",
          buttonText: "Go Home",
          buttonAction: () => navigate("/"),
        };
      case "INACTIVE":
        return {
          icon: <ErrorOutlineIcon sx={{ fontSize: 64, color: "#ed6c02" }} />,
          title: "Restaurant Currently Inactive",
          message: errorData?.message || "This restaurant is currently inactive. Please try again later.",
          buttonText: "Go Home",
          buttonAction: () => navigate("/"),
        };
      case "PENDING":
        return {
          icon: <PaymentIcon sx={{ fontSize: 64, color: "#ed6c02" }} />,
          title: "Payment Pending",
          message: errorData?.message || `This restaurant is in grace period. ${errorData?.daysRemaining || 0} days remaining. Please contact the restaurant owner.`,
          buttonText: "Go Home",
          buttonAction: () => navigate("/"),
        };
      default:
        return {
          icon: <ErrorOutlineIcon sx={{ fontSize: 64, color: "#757575" }} />,
          title: "Restaurant Unavailable",
          message: errorData?.message || "This restaurant is currently unavailable. Please try again later.",
          buttonText: "Go Home",
          buttonAction: () => navigate("/"),
        };
    }
  };

  const config = getErrorConfig();

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          p: 2,
          bgcolor: "#ffffff",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        },
      }}
    >
      <DialogContent sx={{ textAlign: "center", py: 4 }}>
        {/* Icon */}
        <Box sx={{ mb: 3 }}>{config.icon}</Box>

        {/* Title */}
        <Typography
          variant="h5"
          fontWeight={800}
          sx={{ color: "var(--text)", mb: 1 }}
        >
          {config.title}
        </Typography>

        {/* Message */}
        <Typography
          sx={{ color: "var(--text-light)", mb: 3, lineHeight: 1.6 }}
        >
          {config.message}
        </Typography>

        {/* Grace Period Info */}
        {errorData?.status === "PENDING" && errorData?.daysRemaining > 0 && (
          <Alert
            severity="warning"
            sx={{ mb: 3, borderRadius: 2, textAlign: "left" }}
          >
            <Typography variant="body2">
              <strong>Grace Period:</strong> {errorData.daysRemaining} days remaining
            </Typography>
          </Alert>
        )}

        {/* Access Blocked Info */}
        {errorData?.canAccess === false && (
          <Alert
            severity="error"
            sx={{ mb: 3, borderRadius: 2, textAlign: "left" }}
          >
            <Typography variant="body2">
              <strong>Access:</strong> Currently blocked. Please contact the restaurant.
            </Typography>
          </Alert>
        )}

        {/* Action Buttons */}
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="contained"
            startIcon={<HomeIcon />}
            onClick={config.buttonAction}
            sx={{
              bgcolor: "var(--primary)",
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              py: 1.2,
              "&:hover": { bgcolor: "var(--primary-dark)" },
            }}
          >
            {config.buttonText}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={onRetry}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              borderColor: "var(--border)",
              color: "var(--text)",
              px: 3,
              "&:hover": {
                borderColor: "var(--primary)",
                color: "var(--primary)",
              },
            }}
          >
            Try Again
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentErrorDialog;